import React from 'react';
import { Paper, Typography, Box, makeStyles } from '@material-ui/core';
import { BackBar, BackButton } from './BackBar';
import { SizeSelector } from './SizeSelector';
import { ContentLayout } from './ContentLayout';
import AdaptiveNavBar from '../NavBar/AdaptiveNavBar';
import { styleBreakMobile } from './Globals';
import ModalCard from '../AlertModal/ModalCard';
import { PATH_LABS } from '../../../containers/RoutePaths';

const isWatchman = process.env.REACT_APP_THEME_ENV === 'watchman';

const useStyles = makeStyles(theme => ({
    // Give a height hint of 100 percent of the viewport height.
    detailsLayout: {
        minHeight: '100vh',
        [styleBreakMobile(theme)]: {
            paddingTop: theme.spacing(7.5),
        },
    },
    headerBox: {
        boxShadow: '0px 1px 4px rgba(186, 186, 186, 0.25)',
        [styleBreakMobile(theme)]: {
            position: 'fixed',
            width: '100%',
            top: 0,
        },
    },
    popoverDiv: {
        color: 'inherit',
        float: 'left',
        marginLeft: theme.spacing(0.5),
        [styleBreakMobile(theme)]: {
            position: 'absolute',
            top: 0,
            left: '85%',
            float: 'none',
            marginLeft: theme.spacing(0),
        },
    },
    backButton: {
        paddingTop: theme.spacing(10),
    },
    titleDiv: {
        paddingTop: theme.spacing(4) - 1,
    },
}));

// For mobile we're just a back button bar and title.
function HeaderMobile({ backTo, title, infoText, dataCy, onClick }) {
    const classes = useStyles();
    return (
        <>
            <Box mb={4.5} className={classes.headerBox}>
                <BackBar backTo={backTo} onClick={onClick} />
                {backTo === PATH_LABS && isWatchman && (
                    <Box className={classes.popoverDiv}>
                        <ModalCard title={title} infoText={infoText} />
                    </Box>
                )}
            </Box>
            <ContentLayout>
                {title && (
                    <Typography variant="h1" className={classes.titleDiv} data-cy={dataCy ? dataCy : ''}>
                        {title}
                    </Typography>
                )}
            </ContentLayout>
        </>
    );
}

// For desktop, we're the full menu bar, back button and title.
function HeaderDesktop({ backTo, title, infoText, dataCy, onClick, showAlertModal }) {
    const classes = useStyles();

    return (
        <>
            <AdaptiveNavBar showAlertModal={showAlertModal} />
            <ContentLayout>
                <Box mt={2} mb={4.25} className={classes.backButton}>
                    <BackButton backTo={backTo} onClick={onClick} />
                </Box>
                <Typography style={backTo === PATH_LABS ? { float: 'left' } : { float: 'none' }} variant="h1" data-cy={dataCy ? dataCy : ''}>
                    {title}
                </Typography>
                {backTo === PATH_LABS && isWatchman && (
                    <Box className={classes.popoverDiv}>
                        <ModalCard title={title} infoText={infoText} dataCy={dataCy} />
                    </Box>
                )}
            </ContentLayout>
        </>
    );
}

/**
 * Details layout. Includes app bar, back button and details title.
 * Detail layouts are white background.
 * Use a ContentLayout as the container of its children for conventional content padding/sizing.
 *
 * @param {*} backTo The target url of the back button
 * @param {*} title The title to display for the details page.
 */
export function DetailsLayout({ title, infoText, backTo, children, onClick, dataCy, noMobileHeader, showAlertModal }) {
    const mobileHeader = !noMobileHeader ? <HeaderMobile backTo={backTo} dataCy={dataCy} title={title} infoText={infoText} onClick={onClick} /> : null;
    const desktopHeader = <HeaderDesktop backTo={backTo} title={title} infoText={infoText} dataCy={dataCy} onClick={onClick} showAlertModal={showAlertModal} />;

    return (
        <Paper elevation={0} square className={useStyles().detailsLayout}>
            <SizeSelector mobile={mobileHeader} desktop={desktopHeader} />
            {children}
        </Paper>
    );
}
